import { useMemo } from "react";
import type { ProductionHistory, ProductionHistoryWindow } from "@/features/devices/types";
import { summarizeRateWindow, type RateWindowSummary } from "../../chartWidgets";
import { formatWidgetNumber } from "./format";
import type { TimeSeriesReference } from "./TimeSeriesChart";
import { windowSpan } from "./windowPhrase";

export interface RateWindowStats extends RateWindowSummary {
  /** Linha de referencia pronta para o grafico; `null` sem media. */
  reference: TimeSeriesReference | null;
}

const EMPTY: RateWindowStats = { mean: null, min: null, max: null, coverage: 0, reference: null };

/**
 * Cobertura abaixo disto e dita junto da media. Mesma barra da disponibilidade:
 * julgar a janela com amostra inconclusiva e o que o produto nao faz.
 */
export const LOW_COVERAGE_BELOW = 0.9;

/**
 * Resumo da janela de uma serie de leitura (`v`), memoizado pela resposta do
 * historico — o tick ao vivo nao recalcula, e a referencia mantem identidade
 * para o memo do grafico.
 */
export function useRateWindowStats(history: ProductionHistory | null): RateWindowStats {
  return useMemo(() => {
    if (!history) return EMPTY;
    const summary = summarizeRateWindow(history);
    return {
      ...summary,
      reference: summary.mean === null ? null : { value: summary.mean, label: "média" },
    };
  }, [history]);
}

/**
 * Media da janela, extremos e — so quando a janela esta furada — a fracao com
 * leitura. Vive no RODAPE do card, ao lado de "Atualizado ha Xs": o agora a
 * esquerda, o periodo a direita, sem custar uma linha ao grafico. Em card de
 * duas celulas cada linha a mais empurrava os rotulos do eixo para cima do
 * rodape. O numero grande do card continua sendo a leitura atual.
 */
export function TrendStats({
  stats,
  unit,
  decimals,
  window,
}: {
  stats: RateWindowStats;
  unit: string | null;
  decimals: number;
  window: ProductionHistoryWindow;
}) {
  if (stats.mean === null) return null;
  const withUnit = (value: number | null) =>
    `${formatWidgetNumber(value, decimals)}${unit ? ` ${unit}` : ""}`;
  const coveragePercent = Math.round(stats.coverage * 100);
  // A janela ja esta escrita no cabecalho da tendencia ("Ao longo das 24 h");
  // repeti-la aqui fazia o rodape quebrar em duas linhas no card de duas
  // celulas — e a segunda linha empurrava o eixo X para cima do rodape.
  return (
    <span
      className="w-hero__trend-stats"
      title={`Média ${windowSpan(window)}, ponderada pelas amostras de cada intervalo; períodos sem telemetria não entram no cálculo.`}
    >
      <span className="w-hero__trend-stat">
        média <strong>{withUnit(stats.mean)}</strong>
      </span>
      {stats.min !== null && stats.max !== null ? (
        <span className="w-hero__trend-stat">
          mín <strong>{formatWidgetNumber(stats.min, decimals)}</strong> · máx{" "}
          <strong>{formatWidgetNumber(stats.max, decimals)}</strong>
        </span>
      ) : null}
      {stats.coverage < LOW_COVERAGE_BELOW ? (
        <span className="w-hero__trend-stat w-hero__trend-stat--warn">
          {coveragePercent} % da janela com leitura
        </span>
      ) : null}
    </span>
  );
}
